import { ROOT_ID, type Rect, type Scene } from '@flobi/artwork-core';
import type { Camera } from '@flobi/artwork-core/render';
import { MARK } from './movingBoards';

/**
 * The artboard's name over its top left corner, and the press on it that chooses the board.
 *
 * An artboard's empty middle is canvas — a press there starts a band, so a drag can catch
 * the layers on it — which left nowhere on the page itself to take hold of the board: it
 * could only be chosen in Layers. Figma's answer is the one people know, so it is the one
 * here (Tarek, 2026-11-02): the name sits just above the corner, quiet, and a press on it
 * chooses the board, so a drag moves it, its handles resize it and Delete removes it.
 * Shift on the name adds the board to what is chosen, or lets go of it.
 */

/** In CSS pixels: the type size, how far above the board it sits, and how far round it a press still counts. */
export const LABEL = { size: 11, gap: 5, reach: 3 } as const;
/** A board narrower than this on screen gets no name: zoomed that far out it would be wider than what it names. */
const LEAST_BOARD = 24;

/** A name as it was drawn, in CSS pixels from the canvas's top left. */
export interface LabelBox {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** The boards that carry a name: those at the top of the page that can be seen, in the order they are drawn. */
export function labelledBoards(scene: Scene): string[] {
  return scene.childrenOf(ROOT_ID).filter((id) => {
    const node = scene.get(id);
    return node?.type === 'artboard' && node.visible !== false;
  });
}

/** What the name says: the board's own, or what Layers calls one with none. */
export function labelText(name: string | undefined): string {
  const said = (name ?? '').replace(/\s+/g, ' ').trim();
  return said || 'Artboard';
}

/**
 * Where a board's name goes, in CSS pixels: the left of its baseline box and its top, and
 * how wide it may run — the board's own width, less the play mark's room when it has one.
 * Null when the board is too small on screen to carry a name.
 */
export function labelSlot(board: Rect, camera: Camera, height: number, marked = false): { x: number; y: number; room: number } | null {
  const wide = board.width * camera.zoom;
  if (!(wide >= LEAST_BOARD)) return null;
  const room = wide - (marked ? MARK.size + LABEL.gap : 0);
  if (room < LEAST_BOARD / 2) return null;
  return {
    x: (board.x - camera.x) * camera.zoom,
    y: (board.y - camera.y) * camera.zoom - LABEL.gap - height,
    room,
  };
}

/**
 * The name cut to fit, with an ellipsis, when it is wider than the board. Measured, not
 * counted: an "i" and an "M" are not the same width. Halving, so a long name is a handful
 * of measurements rather than one per character.
 */
export function fitLabel(text: string, room: number, measure: (text: string) => number): string {
  if (measure(text) <= room) return text;
  const ellipsis = '…';
  if (measure(ellipsis) > room) return '';
  const chars = Array.from(text);
  let low = 0;
  let high = chars.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measure(chars.slice(0, mid).join('').trimEnd() + ellipsis) <= room) low = mid;
    else high = mid - 1;
  }
  return low === 0 ? ellipsis : chars.slice(0, low).join('').trimEnd() + ellipsis;
}

/**
 * The board whose name is under a point on the screen, if any. Asked from the front, so
 * where two names overlap the one drawn on top takes the press; a little taller than the
 * type itself, since eleven pixels is a small thing to aim at.
 */
export function labelUnder(labels: readonly LabelBox[], at: { x: number; y: number }): string | null {
  for (let index = labels.length - 1; index >= 0; index--) {
    const label = labels[index]!;
    if (
      at.x >= label.x - LABEL.reach &&
      at.x <= label.x + label.width + LABEL.reach &&
      at.y >= label.y - LABEL.reach &&
      at.y <= label.y + label.height + LABEL.reach
    ) return label.id;
  }
  return null;
}
